import { useMemo } from "react";
import {
  Order,
  useGetOrdersInfiniteInfiniteQuery,
  OrdersResponse,
} from "@/shared/api/lod-api";
import { AnnotationFilterParams } from "@/shared/types/filters";

export interface UseAnnotationOrdersInfiniteResult {
  data: Order[];
  isLoading: boolean;
  isFetchingMore: boolean;
  isError: boolean;
  error: unknown;
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  refetch: () => Promise<void>;
  totalCount: number;
}

export function useAnnotationOrdersInfinite(
  activeFilters: AnnotationFilterParams,
  isInitialized = true
): UseAnnotationOrdersInfiniteResult {
  const queryArgs = useMemo(() => {
    const { show_filters: _show_filters, ...apiFilters } = activeFilters;
    return {
      ...apiFilters,
      limit: 20,
    };
  }, [activeFilters]);

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useGetOrdersInfiniteInfiniteQuery(queryArgs, {
    skip: !isInitialized,
  });

  const flattenedData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: OrdersResponse) => page.results);
  }, [data?.pages]);

  const totalCount = useMemo(() => {
    return data?.pages?.[0]?.count || 0;
  }, [data?.pages]);

  const handleFetchMore = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const handleRefetch = async () => {
    await refetch();
  };

  return {
    data: flattenedData,
    isLoading: isLoading,
    isFetchingMore: isFetchingNextPage,
    isError,
    error,
    hasMore: hasNextPage || false,
    fetchMore: handleFetchMore,
    refetch: handleRefetch,
    totalCount,
  };
}
